import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Collapse,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DescriptionIcon from '@mui/icons-material/Description';
import { customersAPI, environmentsAPI, customerDocsAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

function EnvironmentsList({ customer, formatUrl }) {
  const [environments, setEnvironments] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    loadEnvironments();
  }, [customer.id]);

  const loadEnvironments = async () => {
    try {
      const response = await environmentsAPI.listForCustomer(customer.id);
      setEnvironments(response.data);
    } catch (err) {
      console.error('Failed to load environments', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ margin: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const limsEnvs = environments.filter((e) => e.system_type === 'LIMS');
  const sdmsEnvs = environments.filter((e) => e.system_type === 'SDMS');

  const getEnvColor = (envType) => {
    if (envType === 'Development') return 'info';
    if (envType === 'Test') return 'warning';
    if (envType === 'Production') return 'success';
    return 'default';
  };

  return (
    <Box sx={{ margin: 2 }}>
      {limsEnvs.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom component="div">
            LIMS Environments
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2, mb: 2 }}>
            {limsEnvs.map((env) => (
              <Box key={env.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={env.environment_type}
                  size="small"
                  color={getEnvColor(env.environment_type)}
                  sx={{ minWidth: 100 }}
                />
                <Link
                  href={formatUrl(env.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {env.url}
                  <OpenInNewIcon fontSize="small" />
                </Link>
              </Box>
            ))}
          </Box>
        </>
      )}

      {sdmsEnvs.length > 0 && (
        <>
          <Typography variant="subtitle2" gutterBottom component="div">
            SDMS Environments
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
            {sdmsEnvs.map((env) => (
              <Box key={env.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={env.environment_type}
                  size="small"
                  color={getEnvColor(env.environment_type)}
                  sx={{ minWidth: 100 }}
                />
                <Link
                  href={formatUrl(env.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  {env.url}
                  <OpenInNewIcon fontSize="small" />
                </Link>
              </Box>
            ))}
          </Box>
        </>
      )}

      {environments.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No environments configured
        </Typography>
      )}
    </Box>
  );
}

function CustomerList() {
  const navigate = useNavigate();
  const { canCreate, isAdmin, isSupport } = usePermissions();
  const seesDocs = () => isAdmin() || isSupport();
  const [docCounts, setDocCounts] = useState({});
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [showInactive, setShowInactive] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    cust_code: '',
    cust_name: '',
    cl_number: '',
    location: '',
    is_active: true,
    notes: '',
    licence_start_date: '',
  });
  const [filters, setFilters] = useState({
    code: '',
    name: '',
    location: '',
  });

  useEffect(() => {
    loadCustomers();
    loadLocations();
    // Document counts are for Cloud Services administrators and support
    if (seesDocs()) {
      customerDocsAPI.counts().then((r) => setDocCounts(r.data || {})).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.list();
      setCustomers(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load customers');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await customersAPI.locations();
      setLocations(response.data);
    } catch (err) {
      console.error('Failed to load locations', err);
    }
  };

  const toggleRow = (customerId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [customerId]: !prev[customerId],
    }));
  };

  const formatUrl = (url) => {
    if (!url) return '';
    // Add https:// if no protocol is specified
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const handleOpenCreateDialog = () => {
    setCreateForm({
      cust_code: '',
      cust_name: '',
      cl_number: '',
      location: '',
      is_active: true,
      notes: '',
      licence_start_date: '',
    });
    setCreateDialog(true);
  };

  const handleCreateCustomer = async () => {
    try {
      const response = await customersAPI.create(createForm);
      setSuccess('Customer created successfully');
      setCreateDialog(false);
      loadCustomers();
      // Navigate to the new customer detail page
      navigate(`/customers/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create customer');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredCustomers = customers
    .filter(c => showInactive || c.is_active)
    .filter(c => {
      const matchesCode = !filters.code || c.cust_code.toLowerCase().includes(filters.code.toLowerCase());
      const matchesName = !filters.name || c.cust_name.toLowerCase().includes(filters.name.toLowerCase());
      const matchesLocation = !filters.location || (c.location && c.location.toLowerCase().includes(filters.location.toLowerCase()));
      return matchesCode && matchesName && matchesLocation;
    });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">Customers</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {canCreate() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Add Customer
            </Button>
          )}
          <Button
            variant={showInactive ? "contained" : "outlined"}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? "Show Active Only" : "Show All (Including Inactive)"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50px" />
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Contacts</TableCell>
              <TableCell>Patch Emails</TableCell>
              {seesDocs() && <TableCell>Docs</TableCell>}
              <TableCell>Actions</TableCell>
            </TableRow>
            <TableRow>
              <TableCell />
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filter code..."
                  value={filters.code}
                  onChange={(e) => setFilters({ ...filters, code: e.target.value })}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filter name..."
                  value={filters.name}
                  onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  size="small"
                  placeholder="Filter location..."
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  fullWidth
                />
              </TableCell>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell />
              {seesDocs() && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCustomers.map((customer) => (
              <React.Fragment key={customer.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRow(customer.id)}
                    >
                      {expandedRows[customer.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <strong>{customer.cust_code}</strong>
                  </TableCell>
                  <TableCell>{customer.cust_name}</TableCell>
                  <TableCell>{customer.location || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={customer.is_active ? 'Active' : 'Inactive'}
                      color={customer.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={<EmailIcon />}
                      label={customer.active_contacts || 0}
                      color={customer.active_contacts > 0 ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={customer.patch_contacts || 0}
                      color={customer.patch_contacts > 0 ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  {seesDocs() && (
                    <TableCell>
                      <Chip
                        icon={<DescriptionIcon />}
                        label={docCounts[customer.id] || 0}
                        color={docCounts[customer.id] > 0 ? 'primary' : 'default'}
                        size="small"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Tooltip title="Edit Customer & Manage Contacts">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/customers/${customer.id}`)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={expandedRows[customer.id]} timeout="auto" unmountOnExit>
                      <EnvironmentsList customer={customer} formatUrl={formatUrl} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {filteredCustomers.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  {showInactive ? "No customers found" : "No active customers found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Customer Dialog */}
      <Dialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Customer Code"
                value={createForm.cust_code}
                onChange={(e) => setCreateForm({ ...createForm, cust_code: e.target.value })}
                required
                helperText="Unique identifier (e.g., ABC, XYZ)"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Customer Name"
                value={createForm.cust_name}
                onChange={(e) => setCreateForm({ ...createForm, cust_name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="CL Number"
                value={createForm.cl_number}
                onChange={(e) => setCreateForm({ ...createForm, cl_number: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                select
                label="Location"
                value={createForm.location}
                onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="License Start Date"
                type="date"
                value={createForm.licence_start_date}
                onChange={(e) => setCreateForm({ ...createForm, licence_start_date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={createForm.is_active}
                    onChange={(e) => setCreateForm({ ...createForm, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={createForm.notes}
                onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCustomer}
            variant="contained"
            disabled={!createForm.cust_code || !createForm.cust_name}
          >
            Create Customer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CustomerList;
